import api from '../services/api';
import {
  assertVisitReportPdfBlob,
  getVisitReportPdfErrorMessage,
} from './visitReportPdfErrors';
import { renderLetterPreviewHtml } from './visitLetterPreviewRender';

const LETTER_PREVIEW_CSS_FALLBACK = `
  body { font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #374151; margin: 0; padding: 16px; }
  .generate-letter-preview-content {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 24px 28px;
    font-size: 14px;
    line-height: 1.6;
    color: #374151;
  }
  .generate-letter-preview-header,
  .generate-letter-preview-footer {
    font-family: inherit;
    white-space: pre-wrap;
    word-wrap: break-word;
    margin: 0 0 16px 0;
    font-size: 13px;
    color: #4b5563;
  }
  .generate-letter-preview-body { margin: 16px 0; min-height: 1em; }
  .generate-letter-preview-body p { margin: 0 0 8px 0; }
  .generate-letter-preview-body ul,
  .generate-letter-preview-body ol { margin: 8px 0; padding-left: 24px; }
  .follow-up-open-issues-section {
    margin-top: 18px;
    margin-bottom: 24px;
    border-top: 1px solid #d1d5db;
    padding-top: 14px;
    padding-bottom: 8px;
  }
  .follow-up-open-issues-title {
    margin: 0 0 10px;
    font-size: 16px;
    font-weight: 600;
    color: #134252;
  }
  .follow-up-open-issues-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
    font-size: 12px;
  }
  .follow-up-open-issues-table th,
  .follow-up-open-issues-table td {
    border: 1px solid #d1d5db;
    padding: 7px 8px;
    text-align: left;
    vertical-align: top;
  }
  .follow-up-open-issues-table th {
    background-color: #eef2f6;
    color: #134252;
    font-weight: 600;
  }
  .generate-letter-selected-visits-text {
    margin-bottom: 12px;
    font-size: 13px;
    color: #374151;
  }
  .generate-letter-selected-visits-label { font-weight: 600; }
  .visit-letter-downloads-preview {
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .visit-letter-downloads-preview .generate-letter-preview-body,
  .visit-letter-downloads-preview .generate-letter-preview-body * {
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .visit-letter-downloads-preview .follow-up-open-issues-table th,
  .visit-letter-downloads-preview .follow-up-open-issues-table td,
  .visit-letter-downloads-preview table th,
  .visit-letter-downloads-preview table td {
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .letter-report-created-section {
    margin-top: 24px;
    border-top: 1px solid #d1d5db;
    padding-top: 14px;
  }
  .letter-report-created-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
    font-size: 12px;
    margin-bottom: 16px;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .letter-report-created-table th,
  .letter-report-created-table td {
    border: 1px solid #999;
    padding: 7px 10px;
    text-align: left;
    vertical-align: middle;
  }
  .letter-report-created-header {
    background-color: #eef2f6;
    color: #134252;
    font-weight: 700;
    font-size: 12px;
    text-align: left;
  }
  .letter-report-created-label {
    font-weight: 600;
    color: #134252;
    width: 12%;
    white-space: nowrap;
  }
  .letter-report-created-note {
    font-size: 11px;
    color: #555;
    margin-top: 0;
    margin-bottom: 16px;
    padding: 0 2px;
  }
`;

/** PDF-only overrides — appended last so modal scroll rules (60vh) do not clip footer in Puppeteer. */
const LETTER_PREVIEW_PDF_OVERRIDES = `
  /* PDF: undo modal scroll box — allow full content to paginate */
  .generate-letter-preview-content {
    max-height: none !important;
    overflow: visible !important;
    overflow-y: visible !important;
  }
  .visit-letter-downloads-preview {
    overflow-x: visible !important;
  }
  .follow-up-open-issues-section .table-responsive {
    overflow-x: visible !important;
  }
  /* Keep sign-off block intact on one page */
  .generate-letter-preview-footer {
    page-break-inside: avoid;
    break-inside: avoid;
    margin-top: 20px;
  }
  /* Keep Report Created By block intact on one page */
  .letter-report-created-section {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .letter-report-created-table {
    page-break-inside: avoid;
    break-inside: avoid;
  }
`;

const LETTER_PREVIEW_CSS_WITH_OVERRIDES = `${LETTER_PREVIEW_CSS_FALLBACK}${LETTER_PREVIEW_PDF_OVERRIDES}`;

export function collectVisitLetterPdfCss() {
  if (typeof document === 'undefined') return LETTER_PREVIEW_CSS_WITH_OVERRIDES;
  const cssParts = [LETTER_PREVIEW_CSS_FALLBACK];
  Array.from(document.styleSheets || []).forEach((sheet) => {
    try {
      const rules = sheet.cssRules;
      if (!rules) return;
      const text = Array.from(rules)
        .map((rule) => rule.cssText)
        .filter((t) =>
          t.includes('generate-letter-preview')
          || t.includes('follow-up-open-issues')
          || t.includes('generate-letter-selected-visits')
          || t.includes('letter-report-created')
        )
        .join('\n');
      if (text.trim()) cssParts.push(text);
    } catch (_) {
      // Cross-origin stylesheets cannot be read.
    }
  });
  cssParts.push(LETTER_PREVIEW_PDF_OVERRIDES);
  return cssParts.join('\n');
}

export async function assertVisitLetterPdfBlob(blob) {
  return assertVisitReportPdfBlob(blob);
}

export function downloadLetterPdfBlob(blob, fileName) {
  const name = (fileName && String(fileName).trim()) || 'Visit-Letter.pdf';
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name.endsWith('.pdf') ? name : `${name}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function getVisitLetterPdfErrorMessage(err) {
  return getVisitReportPdfErrorMessage(err);
}

export async function downloadVisitLetterPdfFromHtml({ html, fileName, title }) {
  const htmlContent = (html ?? '').toString().trim();
  if (!htmlContent) {
    const e = new Error('Letter preview HTML is empty.');
    e.code = 'EMPTY_PREVIEW';
    throw e;
  }

  const safeFileName = (fileName && String(fileName).trim()) || 'Visit-Letter.pdf';
  const safeTitle = (title && String(title).trim()) || 'Visit Letter';

  const response = await api.post('/pdf/visit-letter', {
    html: htmlContent,
    css: collectVisitLetterPdfCss(),
    title: safeTitle,
    fileName: safeFileName,
  }, {
    responseType: 'blob',
    timeout: 120000,
  });

  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: 'application/pdf' });
  const pdfBlob = await assertVisitLetterPdfBlob(blob);
  downloadLetterPdfBlob(pdfBlob, safeFileName);
  return pdfBlob;
}

export { renderLetterPreviewHtml };
